import { VulnerabilitiesApiGetEolDashboardDetailsRequest } from '@ariksa/inventory-core';
/* Legacy request types are inferred from the compatibility client. */
import {
  ContextApiGetRiskContextResRequest,
  CountSummary,
  EOLDashboard,
  InsightV2Pagination,
  ResourceSummaryApiGetSummaryByTypeRequest,
  RiskContextResponse,
  VulnerabilitiesApiGetEolEntitiesDashboardDetailsRequest,
} from '@ariksa/inventory-core/api';
import { ChartsApiGetVulnerabilitiesDetailsRequest } from '@ariksa/reporting/api';
import flagsmith from '@flagsmith/flagsmith';
import { call } from 'redux-saga/effects';
import { QueryContext } from 'services/utils/QueryContext';
import { takeLatestAction } from 'services/utils/takeLatestAction';
import {
  DefaultApiGetBySourcesDashboardDataRequest,
  DefaultApiGetCriticalAndHighSeveritySummaryRequest,
  DefaultApiGetHighestOccurringVulnerabilitiesRequest,
  DefaultApiGetMostWidelyDeployedLibVulnerabilitiesRequest,
  DefaultApiGetTopNewVulnerabilitiesRequest,
  DefaultApiGetVulnerabilityCountByAgnosticResourceRequest,
} from 'services/Vulnerability/legacyRequestTypes';
import {
  VulnerabilityDashboardBySources,
  VulnerabilityDashboardCount,
  VulnerabilityDashboardCriticalHigh,
  VulnerabilityDashboardHighestItem,
  VulnerabilityDashboardLibraryItem,
  VulnerabilityDashboardTopItem,
} from 'services/Vulnerability/types';

import {
  getScanVaultVulnerabilityOverview,
  getVulnerabilityDashboard,
  mapScanVaultOverviewCount,
  mapScanVaultOverviewItem,
} from 'api/scan-vault/client';
import { mapDashboardToLegacy } from 'api/scan-vault/legacy';
import {
  clampPatchTrendStart,
  getVulnerabilityPatchTrend,
  mapPatchTrendToLegacy,
} from 'api/scan-vault/patchTrend';
import { InventoryService, ReportingService } from 'api/services';
import { LegacyScanAnalysis } from 'api/services/legacyScanAnalysis';
import { doGetVulnerabilityPrioritization } from 'containers/Visibility/Vulnerabilities/saga';

import { actions } from './slice';

export function* vulnerabilityOverviewSaga() {
  //vulnerabilities
  yield takeLatestAction(
    actions.getTopNewVulnerabilities,
    doGetTopNewVulnerabilities,
  );
  yield takeLatestAction(
    actions.getHighestOccurringVulnerabilities,
    doGetHighestOccurringVulnerabilities,
  );
  yield takeLatestAction(
    actions.getMostWidelyDeployedLibraries,
    doGetMostWidelyDeployedLibraries,
  );
  yield takeLatestAction(
    actions.getVulnerableEntitiesWithSensitiveDataAccess,
    doGetVulnerableEntitiesWithSensitiveDataAccess,
  );

  yield takeLatestAction(
    actions.getCriticalAndHighVulnerabilitiesSummary,
    doGetVulnerabilitiesBySeveritySummary,
  );
  yield takeLatestAction(actions.getEOLSoftware, doGetEOLSoftware);
  yield takeLatestAction(actions.getAllEOLSoftware, doGetAllEOLSoftware);
  yield takeLatestAction(actions.getRiskContext, doGetRiskContext);
  yield takeLatestAction(
    actions.getEOLSoftwareEntities,
    doGetEOLSoftwareEntities,
  );
  yield takeLatestAction(
    actions.getVmVulnerabilitiesCount,
    doGetVMVulnerabilitiesCount,
  );
  yield takeLatestAction(
    actions.getVulnerabilitiesCount,
    doGetVulnerabilitiesCount,
  );
  yield takeLatestAction(
    actions.getVmImageVulnerabilitiesCount,
    doGetVMImageVulnerabilitiesCount,
  );
  yield takeLatestAction(
    actions.getContainerServiceVulnerabilitiesCount,
    doGetContainerServiceVulnerabilitiesCount,
  );
  yield takeLatestAction(
    actions.getContainerVulnerabilitiesCount,
    doGetContainerVulnerabilitiesCount,
  );
  yield takeLatestAction(
    actions.getServerlessVulnerabilitiesCount,
    doGetServerlessVulnerabilitiesCount,
  );
  yield takeLatestAction(
    actions.getVulnerabilityPrioritizationData,
    doGetVulnerabilityPrioritization,
  );
  yield takeLatestAction(
    actions.getVulnerabilitiesByResourceTypes,
    doGetVulnerabilitiesByResourceTypes,
  );
  yield takeLatestAction(
    actions.getExploitableEntitiesToPatch,
    doGetExploitableEntitiesToPatch,
  );
}

/*--------------------------vulnerabilities---------------------------------*/

/*get top new vulnerabilities*/
export function* doGetTopNewVulnerabilities(
  ctx: QueryContext<
    VulnerabilityDashboardTopItem[],
    DefaultApiGetTopNewVulnerabilitiesRequest
  >,
) {
  if (flagsmith.hasFeature('new_vuln_dashboard')) {
    yield* fetchScanVaultOverview(ctx, response =>
      response.top_new.map(mapScanVaultOverviewItem),
    );
    return;
  }
  yield call(
    ctx.fetch,
    () => LegacyScanAnalysis.Default.getTopNewVulnerabilities(ctx.params),
    {
      errorMsg: 'Failed to get top new vulnerabilities!',
      cacheKey: 'topNewVulnerabilities',
    },
  );
}

/*get highest occurring vulnerabilities*/
export function* doGetHighestOccurringVulnerabilities(
  ctx: QueryContext<
    VulnerabilityDashboardHighestItem[],
    DefaultApiGetHighestOccurringVulnerabilitiesRequest
  >,
) {
  if (flagsmith.hasFeature('new_vuln_dashboard')) {
    yield* fetchScanVaultOverview(ctx, response =>
      response.highest_occurring.map(mapScanVaultOverviewItem),
    );
    return;
  }
  yield call(
    ctx.fetch,
    () =>
      LegacyScanAnalysis.Default.getHighestOccurringVulnerabilities(ctx.params),
    {
      errorMsg: 'Failed to get highest occurring vulnerabilities!',
      cacheKey: 'highestOccurringVulnerabilities',
    },
  );
}

/*get most widely deployed libraries*/
export function* doGetMostWidelyDeployedLibraries(
  ctx: QueryContext<
    VulnerabilityDashboardLibraryItem[],
    DefaultApiGetMostWidelyDeployedLibVulnerabilitiesRequest
  >,
) {
  if (flagsmith.hasFeature('new_vuln_dashboard')) {
    yield* fetchScanVaultOverview(ctx, response =>
      response.most_widely_deployed.map(mapScanVaultOverviewItem),
    );
    return;
  }
  yield call(
    ctx.fetch,
    () =>
      LegacyScanAnalysis.Default.getMostWidelyDeployedLibVulnerabilities(
        ctx.params,
      ),
    {
      errorMsg: 'Failed to get most widely deployed libraries!',
      cacheKey: 'mostWidelyDeployedLibraries',
    },
  );
}

/*get vulnerable entities with privileged sensitive data access*/
export function* doGetVulnerableEntitiesWithSensitiveDataAccess(
  ctx: QueryContext<CountSummary, ResourceSummaryApiGetSummaryByTypeRequest>,
) {
  yield call(
    ctx.fetch,
    () => InventoryService.ResourceSummary.getSummaryByType(ctx.params),
    {
      errorMsg:
        'Failed to get vulnerable entities with privileged sensitive data access!',
      cacheKey: 'vulnerableEntitiesWithSensitiveDataAccess',
    },
  );
}

/*get critical and high severity for OS and technology*/
export function* doGetVulnerabilitiesBySeveritySummary(
  ctx: QueryContext<
    VulnerabilityDashboardCriticalHigh,
    DefaultApiGetCriticalAndHighSeveritySummaryRequest
  >,
) {
  if (flagsmith.hasFeature('new_vuln_dashboard')) {
    yield* fetchScanVaultOverview(ctx, response => response.critical_high);
    return;
  }
  yield call(
    ctx.fetch,
    () =>
      LegacyScanAnalysis.Default.getCriticalAndHighSeveritySummary(ctx.params),
    {
      errorMsg: 'Failed to get vulnerabilities by severity!',
      cacheKey: 'vulnerabilitiesBySeveritySummary',
    },
  );
}

export function* doGetEOLSoftware(
  ctx: QueryContext<
    EOLDashboard,
    VulnerabilitiesApiGetEolDashboardDetailsRequest
  >,
) {
  yield call(
    ctx.fetch,
    () => InventoryService.Vulnerability.getEolDashboardDetails(ctx.params),
    {
      errorMsg: 'Failed to get end-of-life software!',
      cacheKey: 'eolSoftware',
    },
  );
}

export function* doGetAllEOLSoftware(
  ctx: QueryContext<
    EOLDashboard,
    VulnerabilitiesApiGetEolDashboardDetailsRequest
  >,
) {
  yield call(
    ctx.fetch,
    () => InventoryService.Vulnerability.getEolDashboardDetails(ctx.params),
    {
      errorMsg: 'Failed to get end-of-life software!',
      //cacheKey: 'eolSoftware',
    },
  );
}

export function* doGetEOLSoftwareEntities(
  ctx: QueryContext<
    InsightV2Pagination,
    VulnerabilitiesApiGetEolEntitiesDashboardDetailsRequest
  >,
) {
  yield call(
    ctx.fetch,
    () =>
      InventoryService.Vulnerability.getEolEntitiesDashboardDetails(ctx.params),
    {
      errorMsg: 'Failed to get end-of-life software entities!',
      //cacheKey: 'eolSoftware',
    },
  );
}

export function* doGetVulnerabilitiesCount(
  ctx: QueryContext<
    VulnerabilityDashboardCount,
    DefaultApiGetVulnerabilityCountByAgnosticResourceRequest
  >,
) {
  if (flagsmith.hasFeature('new_vuln_dashboard')) {
    yield* fetchScanVaultOverview(ctx, response =>
      mapScanVaultOverviewCount(response, (ctx.params as any).agnosticResource),
    );
    return;
  }
  yield call(
    ctx.fetch,
    () =>
      LegacyScanAnalysis.Default.getVulnerabilityCountByAgnosticResource(
        ctx.params,
      ),
    {
      errorMsg: 'Failed to get vulnerabilities count!',
      cacheKey: 'vulnerabilityReportingResourcesCount',
    },
  );
}

export function* doGetVMVulnerabilitiesCount(
  ctx: QueryContext<
    VulnerabilityDashboardCount,
    DefaultApiGetVulnerabilityCountByAgnosticResourceRequest
  >,
) {
  if (flagsmith.hasFeature('new_vuln_dashboard')) {
    yield* fetchScanVaultOverview(ctx, response =>
      mapScanVaultOverviewCount(response, (ctx.params as any).agnosticResource),
    );
    return;
  }
  yield call(
    ctx.fetch,
    () =>
      LegacyScanAnalysis.Default.getVulnerabilityCountByAgnosticResource(
        ctx.params,
      ),
    {
      errorMsg: 'Failed to get VM vulnerability count!',
      cacheKey: 'vmVulnerabilityCount',
    },
  );
}

export function* doGetContainerVulnerabilitiesCount(
  ctx: QueryContext<
    VulnerabilityDashboardCount,
    DefaultApiGetVulnerabilityCountByAgnosticResourceRequest
  >,
) {
  if (flagsmith.hasFeature('new_vuln_dashboard')) {
    yield* fetchScanVaultOverview(ctx, response =>
      mapScanVaultOverviewCount(response, (ctx.params as any).agnosticResource),
    );
    return;
  }
  yield call(
    ctx.fetch,
    () =>
      LegacyScanAnalysis.Default.getVulnerabilityCountByAgnosticResource(
        ctx.params,
      ),
    {
      errorMsg: 'Failed to get Container vulnerability count!',
      cacheKey: 'containerVulnerabilityCount',
    },
  );
}

export function* doGetContainerServiceVulnerabilitiesCount(
  ctx: QueryContext<
    VulnerabilityDashboardCount,
    DefaultApiGetVulnerabilityCountByAgnosticResourceRequest
  >,
) {
  if (flagsmith.hasFeature('new_vuln_dashboard')) {
    yield* fetchScanVaultOverview(ctx, response =>
      mapScanVaultOverviewCount(response, (ctx.params as any).agnosticResource),
    );
    return;
  }
  yield call(
    ctx.fetch,
    () =>
      LegacyScanAnalysis.Default.getVulnerabilityCountByAgnosticResource(
        ctx.params,
      ),
    {
      errorMsg: 'Failed to get Container Service vulnerability count!',
      cacheKey: 'containerServiceVulnerabilityCount',
    },
  );
}

export function* doGetVMImageVulnerabilitiesCount(
  ctx: QueryContext<
    VulnerabilityDashboardCount,
    DefaultApiGetVulnerabilityCountByAgnosticResourceRequest
  >,
) {
  if (flagsmith.hasFeature('new_vuln_dashboard')) {
    yield* fetchScanVaultOverview(ctx, response =>
      mapScanVaultOverviewCount(response, (ctx.params as any).agnosticResource),
    );
    return;
  }
  yield call(
    ctx.fetch,
    () =>
      LegacyScanAnalysis.Default.getVulnerabilityCountByAgnosticResource(
        ctx.params,
      ),
    {
      errorMsg: 'Failed to get VM Image vulnerability count!',
      cacheKey: 'vmImageVulnerabilityCount',
    },
  );
}

export function* doGetServerlessVulnerabilitiesCount(
  ctx: QueryContext<
    VulnerabilityDashboardCount,
    DefaultApiGetVulnerabilityCountByAgnosticResourceRequest
  >,
) {
  if (flagsmith.hasFeature('new_vuln_dashboard')) {
    yield* fetchScanVaultOverview(ctx, response =>
      mapScanVaultOverviewCount(response, (ctx.params as any).agnosticResource),
    );
    return;
  }
  yield call(
    ctx.fetch,
    () =>
      LegacyScanAnalysis.Default.getVulnerabilityCountByAgnosticResource(
        ctx.params,
      ),
    {
      errorMsg: 'Failed to get Serverless vulnerability count!',
      cacheKey: 'serverlessVulnerabilityCount',
    },
  );
}

export function* doGetRiskContext(
  ctx: QueryContext<
    Record<string, RiskContextResponse[]>,
    ContextApiGetRiskContextResRequest
  >,
) {
  yield call(
    ctx.fetch,
    () => InventoryService.Context.getRiskContextRes(ctx.params),
    {
      errorMsg: 'Failed to get risk context!',
    },
  );
}

export function* doGetVulnerabilitiesByResourceTypes(
  ctx: QueryContext<
    VulnerabilityDashboardBySources,
    DefaultApiGetBySourcesDashboardDataRequest
  >,
) {
  if (flagsmith.hasFeature('new_vuln_dashboard')) {
    yield call(
      ctx.fetch as any,
      () => getVulnerabilityDashboard((ctx.params as any) as any),
      {
        map: response =>
          mapDashboardToLegacy(response).vulnerabilitiesBySources,
        errorMsg: 'Failed to get vulnerabilities by resource types!',
        cacheKey: 'vulnerabilitiesByResourceTypes',
      },
    );
    return;
  }
  yield call(
    ctx.fetch,
    () => LegacyScanAnalysis.Default.getBySourcesDashboardData(ctx.params),
    {
      errorMsg: 'Failed to get vulnerabilities by resource types!',
      cacheKey: 'vulnerabilitiesByResourceTypes',
    },
  );
}

function* fetchScanVaultOverview(
  ctx: QueryContext<any, any>,
  mapResponse: (response: any) => any,
) {
  yield call(
    ctx.fetch as any,
    () => getScanVaultVulnerabilityOverview((ctx.params as any) ?? {}),
    {
      map: mapResponse,
      errorMsg: 'Failed to get vulnerability overview!',
    },
  );
}

export function* doGetExploitableEntitiesToPatch(
  ctx: QueryContext<any, ChartsApiGetVulnerabilitiesDetailsRequest>,
) {
  if (flagsmith.hasFeature('new_vuln_dashboard')) {
    const params = ctx.params as any;
    yield call(
      ctx.fetch as any,
      () =>
        getVulnerabilityPatchTrend({
          environmentId: params.environmentId,
          accountId: params.accountId,
          startDate: clampPatchTrendStart(params.startDate, params.endDate),
          endDate: params.endDate,
        }),
      {
        map: mapPatchTrendToLegacy,
        errorMsg: 'Failed to get exploitable entities to patch!',
      },
    );
    return;
  }
  yield call(
    ctx.fetch,
    () => ReportingService.Charts.getVulnerabilitiesDetails(ctx.params),
    {
      errorMsg: 'Failed to get exploitable entities to patch!',
      cacheKey: 'exploitableEntitiesToPatch',
    },
  );
}
